import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { APIRequestStatus } from 'common-ts/dist/models/APIRequestStatus';
import Review from 'common-ts/dist/models/Review';
import { RootState } from '../store';
import { terbebasClient } from '../terbebasApi';


interface ProductReviews {
  productId: string | undefined
  fetchProductId: string | undefined
  reviews: Review[]
  averageStars: number | undefined
  postStatus: APIRequestStatus,
  getStatus: APIRequestStatus,
}

// Define the initial state using that type
const initialState: ProductReviews = {
  productId: undefined,
  fetchProductId: undefined,
  reviews: [],
  averageStars: undefined,
  postStatus: APIRequestStatus.Idle,
  getStatus: APIRequestStatus.Idle,
}

// Terbebas review submission. Accepts the legacy Review shape (stars/
// headline/message) plus optional uploaded photo URLs.
export const postNewReview = createAsyncThunk('productReview/postNewReview', async (payload: Review & { imageUrls?: string[] }, thunkAPI) => {
  const rootState = (thunkAPI.getState() as RootState);
  const idToken = rootState.account.authState === "signedin" ? rootState.account.idToken : undefined;

  const response = await terbebasClient.post('/storefront/reviews', {
    productId: payload.productId,
    name: payload.name,
    rating: payload.stars,
    headline: payload.headline,
    message: payload.message,
    imageUrls: payload.imageUrls ?? [],
  }, idToken ? { headers: { 'Authorization': 'Bearer ' + idToken } } : undefined);

  // Re-fetch reviews
  thunkAPI.dispatch(fetchProductReviews(payload.productId));

  return response.data
})

// Terbebas review fetch, mapped back onto the legacy Review shape the
// reducer/UI consume (stars, creationTimestamp in unix seconds).
export const fetchProductReviews = createAsyncThunk('productReview/fetchProductReviews', async (payload: string, thunkAPI) => {
  const response = await terbebasClient.get('/storefront/reviews', {
    params: { productId: payload },
  });

  const reviews = (response.data?.reviews ?? []).map((r: any): Review => ({
    productId: r.productId,
    creationTimestamp: "" + Math.floor(new Date(r.createdAt).getTime() / 1000),
    sourceOfReview: "WEBSITE",
    isVerified: !!r.isVerified,
    name: r.name,
    stars: r.rating,
    headline: r.headline ?? '',
    message: r.message ?? '',
    // listReviewsPublic has always returned these; the mapper used to drop
    // them, which left the review block with no photos to show.
    imageUrls: r.imageUrls ?? [],
  }));

  return reviews;
})

export const productReviewsSlice = createSlice({
  name: 'productReviews',
  initialState,
  reducers: {
    fetchNewProductId: (state, action: PayloadAction<string>) => {
      state.fetchProductId = action.payload;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(postNewReview.pending, (state, action) => {
        state.postStatus = APIRequestStatus.RequestInProgress;
      })
      .addCase(postNewReview.fulfilled, (state, action) => {
        state.postStatus = APIRequestStatus.Success;
      })
      .addCase(postNewReview.rejected, (state, action) => {
        state.postStatus = APIRequestStatus.Failure;
      })
      .addCase(fetchProductReviews.pending, (state, action) => {
        state.getStatus = APIRequestStatus.RequestInProgress;
      })
      .addCase(fetchProductReviews.fulfilled, (state, action) => {

        // Thunk already returns a mapped Review[].
        let reviewsArray: Review[] = action.payload;
        state.reviews = [];

        if (reviewsArray.length === 0) {
          state.productId = state.fetchProductId;
          state.getStatus = APIRequestStatus.Success;
          return;
        }

        let totalstars = 0;
        for (let key in reviewsArray) {
          const review: Review = reviewsArray[key];
          if (review.stars < 1) {
            review.stars = 1;
          } else if (review.stars > 5) {
            review.stars = 5;
          }

          state.reviews.push({...review});

          totalstars = totalstars + review.stars;
        }
        state.averageStars = +(totalstars / reviewsArray.length).toFixed(2);
        // We round the average stars

        state.productId = state.fetchProductId;
        state.getStatus = APIRequestStatus.Success;
      })
      .addCase(fetchProductReviews.rejected, (state, action) => {
        state.getStatus = APIRequestStatus.Failure;
        // state.error = action.error.message
        console.log("fetch is failure");
      })
  }
})

// Action creators are generated for each case reducer function
export const { fetchNewProductId } = productReviewsSlice.actions

export default productReviewsSlice.reducer